import { BUSINESS, FOUNDER, PRICES, SITE_URL, type PriceItem } from '../config/business';
import { COVERAGE_TOWNS } from '../data/coverageTowns';
import type { JsonLd } from './structuredData';

const LOGO_URL = `${SITE_URL}/images/brand/logo-image.png`;

const CATALOG_NAMES: Record<PriceItem['category'], string> = {
  telephone: 'Réparation téléphone',
  informatique: 'Informatique',
  web: 'Création de sites web',
};

const SERVICE_TYPES: Record<PriceItem['category'], string> = {
  telephone: 'Réparation de téléphone',
  informatique: 'Réparation et maintenance informatique',
  web: 'Création de sites web',
};

function buildOffer(item: PriceItem): JsonLd {
  const offer: JsonLd = {
    '@type': 'Offer',
    itemOffered: { '@type': 'Service', name: item.label, serviceType: SERVICE_TYPES[item.category] },
    priceCurrency: 'EUR',
  };
  if (item.priceFrom !== null) {
    offer.priceSpecification = { '@type': 'PriceSpecification', minPrice: item.priceFrom, priceCurrency: 'EUR' };
  }
  return offer;
}

function buildOfferCatalog(): JsonLd {
  const categories = Object.keys(CATALOG_NAMES) as PriceItem['category'][];
  return {
    '@type': 'OfferCatalog',
    name: `Services ${BUSINESS.name}`,
    itemListElement: categories.map((category) => ({
      '@type': 'OfferCatalog',
      name: CATALOG_NAMES[category],
      itemListElement: PRICES.filter((p) => p.category === category).map(buildOffer),
    })),
  };
}

/**
 * Entité LocalBusiness du site, générée depuis la config (business.ts) et la liste des communes
 * (coverageTowns.ts) : mêmes données que la zone d'intervention et le footer.
 * Pas d'adresse postale ni de geo (activité à domicile), pas d'aggregateRating,
 * pas d'openingHoursSpecification (volontairement non publiés).
 */
export function buildLocalBusinessSchema(): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${SITE_URL}/#business`,
    name: BUSINESS.name,
    description:
      'Réparation de téléphones (iPhone, Samsung, Xiaomi, iPad), réparation et maintenance PC et Mac, récupération de données et création de sites web à Viry, près de Genève et de Saint-Julien-en-Genevois. Intervention à domicile, sur rendez-vous.',
    url: `${SITE_URL}/`,
    telephone: BUSINESS.phoneTel,
    email: BUSINESS.email,
    founder: {
      '@type': 'Person',
      name: FOUNDER.name,
      jobTitle: FOUNDER.jobTitle,
      image: `${SITE_URL}${FOUNDER.photo}`,
      sameAs: [FOUNDER.linkedin],
    },
    image: LOGO_URL,
    logo: LOGO_URL,
    priceRange: '€€',
    currenciesAccepted: 'EUR',
    address: {
      '@type': 'PostalAddress',
      addressLocality: BUSINESS.address.city,
      postalCode: BUSINESS.address.postalCode,
      addressRegion: 'Haute-Savoie',
      addressCountry: BUSINESS.address.countryCode,
    },
    areaServed: COVERAGE_TOWNS.map(({ name }) => ({ '@type': 'City', name })),
    serviceType: [
      'Réparation de téléphone',
      'Réparation iPhone',
      'Réparation et maintenance informatique',
      'Récupération de données',
      'Création de sites web',
    ],
    knowsAbout: ['iPhone', 'Samsung', 'Xiaomi', 'iPad', 'PC', 'Mac'],
    sameAs: [...BUSINESS.sameAs],
    hasOfferCatalog: buildOfferCatalog(),
  };
}
